import React, { useState, memo, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { OFFICE_LOCATIONS, POLITICAL_POSITIONS, CONTACT_INFO, VALIDATION_MESSAGES, APP_CONFIG } from '../constants';
import {
  trackFormStart,
  trackFieldInteraction,
  trackValidationError,
  trackSubmissionAttempt,
  trackSubmissionSuccess,
  trackSubmissionFailure,
  trackWhatsAppClick
} from '../utils/formAnalytics';

const Contact = memo(() => {
  const [ref, inView] = useIntersectionObserver();

  // Track form start when component mounts and is in view
  useEffect(() => {
    if (inView) {
      trackFormStart('homepage_contact', 'contact_section');
    }
  }, [inView]);

  const [formData, setFormData] = useState({
    fullName: '',
    politicalPosition: '',
    phone: '',
    constituency: '',
    campaignRequirements: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Track field interaction
    trackFieldInteraction('homepage_contact', name, 'change', value);

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.politicalPosition.trim()) {
      errors.politicalPosition = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.phone.trim()) {
      errors.phone = VALIDATION_MESSAGES.REQUIRED_FIELD;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = VALIDATION_MESSAGES.INVALID_PHONE;
    }

    if (!formData.constituency.trim()) {
      errors.constituency = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.campaignRequirements.trim()) {
      errors.campaignRequirements = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    return errors;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    const isValid = Object.keys(errors).length === 0;

    // Track submission attempt
    trackSubmissionAttempt('homepage_contact', isValid,
      Object.keys(errors).map(field => ({ field, type: 'validation' }))
    );

    if (!isValid) {
      setFormErrors(errors);
      // Track validation errors
      Object.keys(errors).forEach(field => {
        trackValidationError('homepage_contact', field, errors[field]);
      });
      return;
    }

    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    try {
      // Prepare data for Laravel API
      const apiData = {
        name: formData.fullName,
        political_position: formData.politicalPosition,
        phone: formData.phone,
        constituency: formData.constituency,
        campaign_requirements: formData.campaignRequirements,
        source: 'main_page'
      };

      // Submit to Laravel API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), APP_CONFIG.FORM_TIMEOUT);

      const response = await fetch(`${APP_CONFIG.API_BASE_URL}/api/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(apiData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit enquiry');
      }

      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Strategy call request submitted:', result);
      }

      // Track successful submission
      trackSubmissionSuccess('homepage_contact', apiData);

      setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          fullName: '',
          politicalPosition: '',
          phone: '',
          constituency: '',
          campaignRequirements: ''
        });
        setFormStatus({ isSubmitting: false, isSubmitted: false, error: null });
      }, APP_CONFIG.FORM_RESET_DELAY);

    } catch (error) {
      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', error);
      }

      let errorMessage = VALIDATION_MESSAGES.FORM_ERROR;
      let errorType = 'unknown';

      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
        errorType = 'timeout';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
        errorType = 'network';
      } else if (error.message) {
        errorMessage = error.message;
        errorType = 'server';
      }

      // Track submission failure
      trackSubmissionFailure('homepage_contact', errorType, errorMessage);

      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        error: errorMessage
      });
    }
  }, [formData, validateForm]);

  // Handle WhatsApp click
  const handleWhatsAppClick = useCallback(() => {
    // Track WhatsApp click
    trackWhatsAppClick('homepage_contact', formData);

    const whatsappUrl = `${CONTACT_INFO.WHATSAPP_URL}?text=${encodeURIComponent(CONTACT_INFO.WHATSAPP_MESSAGE)}`;
    window.open(whatsappUrl, '_blank');
  }, [formData]);

  return (
    <section id="contact" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <motion.div
            className="inline-flex items-center space-x-3 bg-jetBlue-100 text-jetBlue-800 rounded-full px-6 py-3 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <span className="text-xl">📞</span>
            <span className="font-semibold tracking-wide">BOOK YOUR STRATEGY CALL</span>
          </motion.div>

          <motion.h2
            className="display-text text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Ready to Win Your <span className="text-gradient-political">Next Election?</span>
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Schedule a confidential strategy consultation with India's leading political branding experts.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch with Our Political Experts</h3>

            {/* Side-by-side office layout */}
            <div className="space-y-8">
              {OFFICE_LOCATIONS.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {row.map((office, officeIndex) => (
                    <motion.div
                      key={office.title}
                      className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      initial={{ opacity: 0, x: -50 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.6, delay: 0.8 + (rowIndex * 2 + officeIndex) * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className={`w-12 h-12 ${office.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <span className={`${office.iconColor} text-xl`}>{office.icon}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">{office.title}</h4>
                        {office.details.map((detail, idx) => (
                          <div key={idx}>
                            {typeof detail === 'string' ? (
                              <p className="text-gray-600 text-sm leading-relaxed">{detail}</p>
                            ) : (
                              <div className="mb-1">
                                {detail.type === 'phone' ? (
                                  <a
                                    href={detail.link}
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                  >
                                    📞 {detail.text}
                                  </a>
                                ) : detail.type === 'email' ? (
                                  <a
                                    href={detail.link}
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                                  >
                                    ✉️ {detail.text}
                                  </a>
                                ) : (
                                  <p className="text-gray-600 text-sm">{detail.text}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <motion.div
              className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white mt-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 1.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center space-x-4">
                {/* WhatsApp Logo SVG */}
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-7 h-7 fill-white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Instant Political Consultation</h4>
                  <p className="text-green-100 text-sm">Get immediate response from our strategy team</p>
                </div>
              </div>
              <motion.button
                className="mt-4 bg-white text-green-600 px-6 py-2 rounded-lg font-medium hover:bg-green-50 transition-colors flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleWhatsAppClick}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-5 h-5 fill-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
                </svg>
                <span>WhatsApp Our Political Team</span>
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Strategy Call Form */}
          <motion.div
            className="bg-gradient-to-br from-jetBlue-50 to-red-50 rounded-3xl p-8"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Book Your Strategy Call</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-jetBlue-500 focus:border-transparent transition-all ${
                      formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Your full name"
                  />
                  {formErrors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.1 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">Political Position *</label>
                  <select
                    name="politicalPosition"
                    value={formData.politicalPosition}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-jetBlue-500 focus:border-transparent transition-all ${
                      formErrors.politicalPosition ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {POLITICAL_POSITIONS.map((position) => (
                      <option key={position.value} value={position.value}>
                        {position.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.politicalPosition && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.politicalPosition}</p>
                  )}
                </motion.div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-jetBlue-500 focus:border-transparent transition-all ${
                      formErrors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+91 98765 43210"
                    maxLength="15"
                  />
                  {formErrors.phone && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.3 }}
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">Constituency *</label>
                  <input
                    type="text"
                    name="constituency"
                    value={formData.constituency}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-jetBlue-500 focus:border-transparent transition-all ${
                      formErrors.constituency ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Bhopal, Indore, Delhi East"
                    maxLength="100"
                  />
                  {formErrors.constituency && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.constituency}</p>
                  )}
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.4 }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Requirements *</label>
                <textarea
                  name="campaignRequirements"
                  value={formData.campaignRequirements}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-jetBlue-500 focus:border-transparent resize-none transition-all ${
                    formErrors.campaignRequirements ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Tell us about your political goals, campaign requirements, target audience, budget expectations, and timeline..."
                  maxLength="1000"
                ></textarea>
                {formErrors.campaignRequirements && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.campaignRequirements}</p>
                )}
              </motion.div>

              <motion.button
                type="submit"
                disabled={formStatus.isSubmitting}
                className={`w-full font-bold py-4 px-6 rounded-2xl transition-all duration-300 ${
                  formStatus.isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : formStatus.isSubmitted
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gradient-to-r from-jetBlue-600 to-red-600 hover:from-jetBlue-700 hover:to-red-700'
                } text-white`}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.5 }}
                whileHover={!formStatus.isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!formStatus.isSubmitting ? { scale: 0.98 } : {}}
              >
                {formStatus.isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting Request...
                  </span>
                ) : formStatus.isSubmitted ? (
                  <span className="flex items-center justify-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Request Submitted Successfully!
                  </span>
                ) : (
                  'Schedule Confidential Strategy Call →'
                )}
              </motion.button>

              {/* Success/Error Messages */}
              {formStatus.isSubmitted && (
                <motion.div
                  className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-green-800 text-sm">
                    ✅ {VALIDATION_MESSAGES.FORM_SUCCESS}
                  </p>
                </motion.div>
              )}

              {formStatus.error && (
                <motion.div
                  className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-red-800 text-sm">
                    ❌ {formStatus.error}
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

export default Contact;
